import React, { useState, useEffect } from 'react';
import { api } from '../utils/apiClient';
import {
  Search, Loader2, AlertCircle, Check,
  FilterX, BarChart3, Mail, FileSpreadsheet
} from 'lucide-react';
import DashboardJudicial from './DashboardJudicial';
import SeguimientoEquidad from './SeguimientoEquidad';


const ESTADOS_GESTION_API = ["Gestionado", "Audiencia", "Aplazado", "Seguimiento", "Cancelado", "Conciliacion"];

const SeguimientoJudicial = ({ readOnly = false }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDashboard, setShowDashboard] = useState(false);
  const [showEquidad, setShowEquidad] = useState(false);

  const [filters, setFilters] = useState({
    gestionado: 'Todos',
    fecha_inicio: '',
    fecha_fin: ''
  });

  const [updatingId, setUpdatingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ text: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  const fetchCases = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.gestionado !== 'Todos') params.gestionado = filters.gestionado;
      const response = { data: await api.get('/casos-judiciales', { params }) };
      setCases((response.data || []).map(c => {
        const lower = (c.aseguradora || '').toLowerCase().trim();
        if (lower.includes('asis') && lower.includes('prex')) return { ...c, aseguradora: 'Assisprex' };
        return c;
      }));
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchCases(); }, [filters.gestionado]);

  const showFeedback = (text, type) => {
    setStatusMsg({ text, type });
    const dur = type === 'warning' ? 8000 : 3000;
    setTimeout(() => setStatusMsg({ text: '', type: '' }), dur);
  };

  const _formatExito = (label, data) => {
    const tablas = (data && Array.isArray(data.tablas_actualizadas)) ? data.tablas_actualizadas : null;
    const warnings = (data && Array.isArray(data.warnings)) ? data.warnings : [];
    if (warnings.length > 0) {
      return { msg: `${label} con advertencias: ${warnings.join(' | ')}`, type: 'warning' };
    }
    if (tablas && tablas.length > 1) {
      return { msg: `${label} (${tablas.length} aseguradoras: ${tablas.join(', ')})`, type: 'success' };
    }
    return { msg: label, type: 'success' };
  };

  const handleUpdateEmail = async (expediente, nuevoEmail) => {
    if (!nuevoEmail || !nuevoEmail.includes('@')) return;
    setUpdatingId(`${expediente}-email`);
    try {
      const resp = { data: await api.patch(`/casos-judiciales/${expediente}/email-usuario`, { email_usuario: nuevoEmail }) };
      setCases(prev => prev.map(c => c.expediente === expediente ? { ...c, email_usuario: nuevoEmail } : c));
      const r = _formatExito("Email actualizado", resp && resp.data);
      showFeedback(r.msg, r.type);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error email';
      showFeedback(msg, "error");
    } finally { setUpdatingId(null); }
  };

  const handleUpdateObservacion = async (expediente, nuevaObs) => {
    setUpdatingId(`${expediente}-obs`);
    try {
      const resp = { data: await api.patch(`/casos-judiciales/${expediente}/observacion`, { observacion: nuevaObs }) };
      setCases(prev => prev.map(c => c.expediente === expediente ? { ...c, observacion: nuevaObs } : c));
      const r = _formatExito("Nota guardada", resp && resp.data);
      showFeedback(r.msg, r.type);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error nota';
      showFeedback(msg, "error");
    } finally { setUpdatingId(null); }
  };

  const handleUpdateFecha = async (expediente, nuevaFecha) => {
    if (!nuevaFecha) return;
    setUpdatingId(`${expediente}-fecha`);
    try {
      const fechaISO = new Date(nuevaFecha).toISOString();
      const resp = { data: await api.put(`/casos-judiciales/${expediente}/fecha-sesion`, { fecha: fechaISO }) };
      setCases(prev => prev.map(c => c.expediente === expediente ? { ...c, fecha_sesion: fechaISO } : c));
      const r = _formatExito("Audiencia agendada", resp && resp.data);
      showFeedback(r.msg, r.type);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al actualizar fecha de audiencia';
      showFeedback(msg, "error");
    } finally { setUpdatingId(null); }
  };

  const handleUpdateGestionado = async (expediente, nuevoEstado) => {
    if (!nuevoEstado) return;
    setUpdatingId(`${expediente}-status`);
    try {
      const resp = { data: await api.patch(`/casos-judiciales/${expediente}/gestionado`, { gestionado: nuevoEstado }) };
      setCases(prev => prev.map(c => c.expediente === expediente ? { ...c, gestionado: nuevoEstado } : c));
      const r = _formatExito("Estado actualizado", resp && resp.data);
      showFeedback(r.msg, r.type);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error estado';
      showFeedback(msg, "error");
    } finally { setUpdatingId(null); }
  };

  const filteredCases = cases.filter((caso) => {
    const matchesSearch = !searchTerm || Object.values(caso).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()));
    let matchesDate = true;
    if (caso.fecha) {
        const fechaCaso = new Date(caso.fecha).getTime();
        const inicio = filters.fecha_inicio ? new Date(filters.fecha_inicio).getTime() : null;
        const fin = filters.fecha_fin ? new Date(filters.fecha_fin).getTime() : null;
        if (inicio && fechaCaso < inicio) matchesDate = false;
        if (fin && fechaCaso > (fin + 86400000)) matchesDate = false;
    }
    return matchesSearch && matchesDate;
  });

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filters.gestionado, filters.fecha_inicio, filters.fecha_fin]);

  const totalPages = Math.ceil(filteredCases.length / ITEMS_PER_PAGE);
  const paginatedCases = filteredCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const inputBase = 'w-full rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-600 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/70 transition-all duration-200';

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 to-slate-900 text-slate-100 p-4 lg:p-5 gap-4 overflow-hidden">

      <DashboardJudicial isOpen={showDashboard} onClose={() => setShowDashboard(false)} data={filteredCases} />
      <SeguimientoEquidad isOpen={showEquidad} onClose={() => setShowEquidad(false)} readOnly={readOnly} />

      {statusMsg.text && (
        <div className={`fixed bottom-10 right-10 z-[200] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border backdrop-blur-md ${statusMsg.type === 'success' ? 'bg-emerald-900/90 border-emerald-500/60 text-emerald-50' : statusMsg.type === 'warning' ? 'bg-amber-900/90 border-amber-500/60 text-amber-50' : 'bg-red-900/90 border-red-500/60 text-red-50'}`}>
          {statusMsg.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          <span className="font-semibold text-xs uppercase tracking-wider">{statusMsg.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-none">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-white tracking-tight">Seguimiento Judicial</h1>
          <p className="text-[10px] text-slate-500 font-bold tracking-[0.22em] uppercase mt-1">Operaciones · Audiencias</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEquidad(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg shadow-emerald-900/30 transition-all active:scale-95"
          >
            <FileSpreadsheet size={14} /> Control Equidad
          </button>
          <button
            onClick={() => setShowDashboard(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg shadow-blue-900/30 transition-all active:scale-95"
          >
            <BarChart3 size={14} /> Dashboard
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex-none rounded-2xl bg-slate-800/40 border border-slate-700/40 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 block">Búsqueda Global</label>
            <Search size={14} className="absolute left-3 top-[34px] text-slate-500" />
            <input
              type="text"
              placeholder="Expediente, placa, nombre..."
              className={`${inputBase} pl-9`}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 block">Desde</label>
            <input type="date" value={filters.fecha_inicio} className={inputBase} onChange={(e) => setFilters({...filters, fecha_inicio: e.target.value})} />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 block">Hasta</label>
            <input type="date" value={filters.fecha_fin} className={inputBase} onChange={(e) => setFilters({...filters, fecha_fin: e.target.value})} />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1 block">Estado</label>
            <select className={`${inputBase} font-medium`} value={filters.gestionado} onChange={(e) => setFilters({...filters, gestionado: e.target.value})}>
              <option value="Todos">Todos</option>
              {ESTADOS_GESTION_API.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            {(filters.fecha_inicio || filters.fecha_fin) && (
              <button
                onClick={() => setFilters({...filters, fecha_inicio: '', fecha_fin: ''})}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
              >
                <FilterX size={14} /> Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl h-full flex items-center justify-center">
            <Loader2 size={28} className="animate-spin text-blue-400" />
          </div>
        ) : paginatedCases.length === 0 ? (
          <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl h-full flex items-center justify-center">
            <div className="flex flex-col items-center text-center max-w-md px-6">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
                <BarChart3 size={24} className="text-blue-400" />
              </div>
              <h3 className="text-xs font-bold tracking-[0.22em] uppercase text-slate-300 mb-2">Sin casos en seguimiento</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                {searchTerm || filters.gestionado !== 'Todos' || filters.fecha_inicio || filters.fecha_fin
                  ? 'Los filtros activos no arrojan resultados. Ajuste fechas o estado para ver más casos.'
                  : 'Aún no hay expedientes judiciales cargados en este entorno. Los casos aparecerán aquí automáticamente.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-3xl border border-slate-700/50 bg-slate-900/90 shadow-xl">
              <div className="overflow-auto flex-1 custom-scrollbar">
                <table className="min-w-max w-full border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/70 sticky top-0 z-20">
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky left-0 bg-slate-800/90 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.3)] whitespace-nowrap">Expediente</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-emerald-400 whitespace-nowrap min-w-[210px]">Email Responsable</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-amber-300 whitespace-nowrap min-w-[260px]">Notas de Seguimiento</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Aseguradora</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cliente Corp.</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Nombre</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Teléfono</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Placa</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Dirección</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Fecha Entrada</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Asunto</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Tipo Servicio</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cierre Servicio</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Cuenta</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Conciliación</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Responsabilidad</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">C. Choque</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Abogado</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">H. Asignación</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">H. Llegada</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-blue-400 whitespace-nowrap min-w-[210px]">Agendar Audiencia</th>
                      <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky right-0 bg-slate-800/90 z-30 shadow-[-2px_0_4px_rgba(0,0,0,0.3)] whitespace-nowrap">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCases.map((caso) => (
                      <tr
                        key={caso.expediente}
                        className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-150 group"
                      >
                        <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-slate-900/95 group-hover:bg-slate-800/60 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.2)] transition-colors">
                          <span className="text-xs font-bold font-mono text-blue-300 uppercase">{caso.expediente}</span>
                        </td>

                        <td className="px-4 py-3">
                          {readOnly
                            ? <span className="text-xs text-slate-400">{caso.email_usuario || '—'}</span>
                            : <div className="flex items-center gap-2">
                                <div className="relative flex-1 min-w-[180px]">
                                  <Mail size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-500/60" />
                                  <input
                                    type="email"
                                    disabled={updatingId === `${caso.expediente}-email`}
                                    placeholder="usuario@legal.com"
                                    className="w-full pl-7 pr-2 py-1.5 bg-slate-800 border border-emerald-500/20 rounded-lg text-[11px] text-slate-100 outline-none focus:ring-1 focus:ring-emerald-500/60 focus:border-emerald-500/40 transition-all"
                                    defaultValue={caso.email_usuario || ''}
                                    onBlur={(e) => handleUpdateEmail(caso.expediente, e.target.value)}
                                  />
                                </div>
                                {updatingId === `${caso.expediente}-email` && <Loader2 size={12} className="animate-spin text-emerald-400" />}
                              </div>
                          }
                        </td>

                        <td className="px-4 py-3">
                          {readOnly
                            ? <span className="text-xs text-slate-400">{caso.observacion || '—'}</span>
                            : <textarea
                                disabled={updatingId === `${caso.expediente}-obs`}
                                rows="1"
                                className="w-full bg-slate-800/60 border border-amber-500/15 rounded-lg p-2 text-[11px] text-slate-300 outline-none focus:ring-1 focus:ring-amber-500/50 focus:border-amber-500/40 resize-none min-h-[34px] transition-all"
                                defaultValue={caso.observacion || ''}
                                onBlur={(e) => handleUpdateObservacion(caso.expediente, e.target.value)}
                              />
                          }
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300">{caso.aseguradora}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-slate-500">{caso.cliente_corporativo}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-200 font-medium uppercase">{caso.nombre}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 font-mono tabular-nums">{caso.telefono}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-blue-300 font-mono font-bold tracking-wider uppercase">{caso.placa || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-400"><div className="w-40 truncate" title={caso.direccion}>{caso.direccion || '—'}</div></td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 font-mono tabular-nums">{caso.fecha}</td>
                        <td className="px-4 py-3 text-xs text-slate-300"><div className="w-40 truncate" title={caso.asunto}>{caso.asunto}</div></td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">{caso.tipo_servicio}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">{caso.cierre_servicio}</td>
                        <td className="px-4 py-3 text-xs text-purple-300 font-medium"><div className="w-32 truncate" title={caso.cuenta}>{caso.cuenta || '—'}</div></td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">{caso.conciliacion}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">{caso.responsabilidad}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 font-mono">{caso.cierre_choque_simple || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-amber-300 font-medium">{caso.abogado || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500 font-mono tabular-nums">{caso.hora_asignacion}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500 font-mono tabular-nums">{caso.hora_llegada}</td>

                        <td className="px-4 py-3">
                          {readOnly
                            ? <span className="text-xs text-slate-400 font-mono tabular-nums">{caso.fecha_sesion?.substring(0, 16) || '—'}</span>
                            : <div className="flex items-center gap-2">
                                <input
                                  type="datetime-local"
                                  disabled={updatingId === `${caso.expediente}-fecha`}
                                  className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500/60 focus:border-blue-500/40 tabular-nums transition-all"
                                  defaultValue={caso.fecha_sesion?.substring(0, 16)}
                                  onBlur={(e) => handleUpdateFecha(caso.expediente, e.target.value)}
                                />
                                {updatingId === `${caso.expediente}-fecha` && <Loader2 size={12} className="animate-spin text-blue-400" />}
                              </div>
                          }
                        </td>

                        <td className="px-4 py-3 text-center sticky right-0 bg-slate-900/95 group-hover:bg-slate-800/60 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.2)] transition-colors">
                          {readOnly
                            ? <span className={`inline-block text-[10px] font-bold py-1.5 px-3 rounded-xl uppercase tracking-wider ${caso.gestionado && caso.gestionado !== "PENDIENTE" ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>{caso.gestionado || 'Pendiente'}</span>
                            : <select
                                disabled={updatingId === `${caso.expediente}-status`}
                                value={caso.gestionado || ''}
                                onChange={(e) => handleUpdateGestionado(caso.expediente, e.target.value)}
                                className={`text-[10px] font-bold py-1.5 px-3 rounded-xl uppercase tracking-wider w-40 cursor-pointer transition-all ${caso.gestionado && caso.gestionado !== "PENDIENTE" ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:border-rose-500/40'}`}
                              >
                                <option value="">— Seleccionar —</option>
                                {ESTADOS_GESTION_API.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 mt-2 rounded-2xl border border-slate-700/40 bg-slate-900/60 flex-shrink-0">
                <span className="text-xs text-slate-500 text-center sm:text-left tabular-nums">
                  {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredCases.length)} de {filteredCases.length} casos
                </span>
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">«</button>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    const page = start + i;
                    return page <= totalPages ? (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${page === currentPage ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>{page}</button>
                    ) : null;
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">›</button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">»</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeguimientoJudicial;
